import { GraphQLClient } from '@mineko/graphql-client';
import { get as getCookie } from 'js-cookie';

const CSRF_HEADER = 'x-csrf';

let client: Record<string, GraphQLClient> = {};

export type Endpoint = { name: string; url: string };

export const initClient = (endpoint: string | Endpoint[]): void => {
  if (Array.isArray(endpoint)) {
    client = endpoint.reduce((acc, { name, url }) => {
      acc[name] = new GraphQLClient(url);
      return acc;
    }, {} as Record<string, GraphQLClient>);
  } else {
    client['default'] = new GraphQLClient(endpoint);
  }
};

export const getClient = (name = 'default'): GraphQLClient => {
  if (!client) {
    throw Error('Client needs to be initialised with the api url');
  }

  if (!client[name]) {
    throw Error(`Client with name ${name} does not exist`);
  }

  return client[name];
};

const setCSRFHeader = (client: GraphQLClient, cookieName: string): void => {
  client.setHeader(CSRF_HEADER, getCookie(cookieName ?? '') ?? '');
};

const handleError =
  (throwOnUnauthorized?: boolean, skipRedirectOnUnauthorized?: boolean) =>
  (error: any): void => {
    if (error?.response?.status === 401) {
      const redirectUrl = error.response.data?.error?.redirectUrl ?? process.env.REACT_APP_LOGIN_URL;
      if (throwOnUnauthorized) {
        throw { name: 'UnauthorizedError', redirectUrl };
      }

      !skipRedirectOnUnauthorized && window.location.assign(redirectUrl);
      return;
    }
    const e = new Error();
    e.message = `${error?.response?.errors?.[0].errorType}: ${error?.response?.errors?.[0].message}`;
    e.name = 'ApiError';
    throw e;
  };

export const runQueryV2: (args: {
  query: string;
  variables?: any;
  cacheResponse?: boolean | undefined;
  httpMethod?: string;
  cookieName?: string;
  clientName?: string;
  throwOnUnauthorized?: boolean;
  skipRedirectOnUnauthorized?: boolean;
}) => Promise<unknown> = ({
  query,
  variables,
  cacheResponse,
  httpMethod,
  cookieName,
  clientName,
  throwOnUnauthorized,
  skipRedirectOnUnauthorized,
}) => {
  const client = getClient(clientName);
  cookieName && setCSRFHeader(client, cookieName);
  return client
    .runQuery(query, variables, cacheResponse, httpMethod)
    .catch(handleError(throwOnUnauthorized, !!skipRedirectOnUnauthorized));
};

export const runQuery: (
  query: string,
  variables?: any,
  cacheResponse?: boolean | undefined,
  httpMethod?: string,
  cookieName?: string,
  clientName?: string,
  throwOnUnauthorized?: boolean,
) => Promise<unknown> = (query, variables, cacheResponse, httpMethod, cookieName, clientName, throwOnUnauthorized) => {
  const client = getClient(clientName);
  cookieName && setCSRFHeader(client, cookieName);
  return client.runQuery(query, variables, cacheResponse, httpMethod).catch(handleError(throwOnUnauthorized));
};

export const runMutation: (
  query: string,
  variables?: any,
  cookieName?: string,
  clientName?: string,
) => Promise<unknown> = (query, variables, cookieName, clientName) => {
  const client = getClient(clientName);
  cookieName && setCSRFHeader(client, cookieName);
  return client.runMutation(query, variables).catch(handleError());
};

export default getClient;
